import { Alert, Card, Collapse, Divider, Flex, Pagination } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Server } from '../../../../../../api/server-index';
import { AuthContext } from '../../../../../../context/auth-context';
import { SearchDataSelection } from '../../../../../../types/common/search-data-selection.type';
import { PaginatedData } from '../../../../../../types/dto/common.dto';
import { SubjectDto } from '../../../../../../types/dto/subject.dto';
import { UserDto } from '../../../../../../types/dto/user.dto';
import SubjectPrograms from './subject-programs/subject-programs.component';

interface LecturerSubjectsProps {
  user: UserDto;
}

const LecturerSubjects = (props: LecturerSubjectsProps) => {
  const { language, currentAcademicYear } = useContext(AuthContext);
  const { t } = useTranslation(['user', 'programs']);
  const [loading, setLoading] = useState<boolean>(true);
  const [subjects, setSubjects] = useState<PaginatedData<SubjectDto[]>>();
  const [dataSelection, setDataSelection] = useState<SearchDataSelection>({
    page: 1,
    pageSize: 10,
  });

  const fetchSubjects = useCallback(async () => {
    setLoading(true);
    const response = await Server.Subjects.getSubjects({
      page: dataSelection?.page,
      perPage: dataSelection?.pageSize,
      lecturerPin: props.user.pin,
      academicYear: currentAcademicYear,
    });

    setSubjects(response);
    setLoading(false);
  }, [currentAcademicYear, dataSelection, props.user.pin]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const renderMoodleAlert = (numberOfMoodleCourses: number) => {
    switch (numberOfMoodleCourses) {
      case 0:
        return (
          <Alert
            showIcon
            type='error'
            description={t('programs:subjects.moodleAlert0')}
          />
        );
      case 1:
        return (
          <Alert
            showIcon
            type='warning'
            description={t('programs:subjects.moodleAlert1')}
          />
        );
      default:
        return (
          <Alert
            showIcon
            type='info'
            description={t('programs:subjects.moodleAlertMultiple', {
              number: numberOfMoodleCourses,
            })}
          />
        );
    }
  };

  return (
    <Card loading={loading} className='shadow-sm'>
      {subjects && subjects.records.length > 0 ? (
        <>
          <Collapse
            className='mt-05'
            accordion
            destroyInactivePanel
            items={subjects.records.map((subject) => ({
              key: subject.id,
              label: (
                <span>
                  {language === 'en'
                    ? subject.subjectNameEn ?? subject.subjectName
                    : subject.subjectName}
                </span>
              ),
              children: (
                <Flex vertical>
                  <h4 className='mt-05 mb-05'>
                    {t('programs:subject_programs')}
                  </h4>
                  {subject.programs ? (
                    <SubjectPrograms programs={subject.programs} />
                  ) : (
                    <></>
                  )}
                  <Divider />

                  <Divider />
                  <h4 className='mt-05 mb-05'>
                    {t('programs:subjects.moodle')}
                  </h4>
                  {renderMoodleAlert(subject.numberOfMoodleCourses!)}
                </Flex>
              ),
            }))}
          />
          <Flex justify='end'>
            <Pagination
              hideOnSinglePage
              size='small'
              className='mt-05'
              current={dataSelection.page}
              total={subjects.totalCount}
              pageSize={dataSelection.pageSize}
              onChange={(page) =>
                setDataSelection((prevData) => ({ ...prevData, page: page }))
              }
              showSizeChanger={false}
            />
          </Flex>
        </>
      ) : (
        <span>{`${t('lecturer_subjects.no_subjects')} (${currentAcademicYear}/${
          currentAcademicYear + 1
        })`}</span>
      )}
    </Card>
  );
};

export default LecturerSubjects;
